<script setup>
import { tv } from 'tailwind-variants';
import { useAttrs, useListeners } from 'vue';

/**
 * @typedef {object} BadgeProps
 * @property {('default'|'info'|'success'|'attention'|'warning'|'critical'|'contrast'|'info-strong'|'success-strong'|'attention-strong'|'warning-strong'|'critical-strong'|'magic')} [variant='default'] - The visual style of the badge.
 * @property {('sm'|'md'|'lg')} [size='md'] - The size of the badge.
 */

/**
 * @type {BadgeProps}
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
  },
  size: {
    type: String,
    default: 'md',
  },
});

const badge = tv({
  base: 'inline-flex items-center justify-center rounded-lg text-xs font-medium',
  variants: {
    variant: {
      default:
        'bg-brand-black-alpha-6 dark:bg-brand-white-alpha-6 text-brand-gray-13 dark:text-brand-gray-8',
      info: 'bg-brand-azure-5 text-brand-azure-15',
      success: 'bg-brand-green-4 text-brand-green-14',
      attention: 'bg-brand-yellow-5 text-brand-yellow-14',
      warning: 'bg-brand-orange-7 text-brand-orange-14',
      critical: 'bg-brand-red-7 text-brand-red-14',
      contrast: 'bg-brand-gray-16 text-brand-gray-1',
      'info-strong': 'bg-brand-fill-info text-brand-azure-16',
      'success-strong': 'bg-brand-fill-success text-brand-green-1',
      'attention-strong': 'bg-brand-fill-caution text-brand-yellow-15',
      'warning-strong': 'bg-brand-fill-warning text-brand-orange-14',
      'critical-strong': 'bg-brand-fill-critical text-brand-red-1',
      custom: 'bg-[var(--cs-badge-background)] text-[var(--cs-badge-text)]',
      magic: 'bg-brand-surface-magic-active text-c-text-magic-secondary',
    },
    size: {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2 py-0.5',
      lg: 'px-2 py-0.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const attrs = useAttrs();
const listeners = useListeners();
</script>

<template>
  <span
    data-component="CsBadge"
    :class="badge(props)"
    v-bind="attrs"
    v-on="listeners"
  >
    <slot />
  </span>
</template>
